import React, {useEffect, useState} from "react";
import {
    AddFacilityPayload,
    AddFacilityUserInput,
    AddFactoryPayload,
    AddFactoryUserInput,
    EditFacilityUserInput,
    EditFactoryUserInput,
    Factory, FactoryGameType,
    IFacility,
    Item,
    ItemStack
} from "../types";
import {FacilityOverview} from "./FacilityOverview";
import './FacilityDetailView.sass';
import {Button, ButtonTypes} from "../../react/components/ui/spectre/buttons";
import {getTranslator, translate} from "../../translate";
import {FacilityIOSummary} from "./FacilityIOSummary";
import FacilityPath from "./FacilityPath";
import FacilityAddEditModal from "./FacilityAddEditModal/Modal";
import {SatisfactoryAPI} from "../../api/FactoryGame/index";
import {Icon} from "../../react/components/ui/spectre/icon";
import {editFacility} from "../Helper/EditFacility";
import {FacilityIOButton} from "./FacilityIOButton";
import {getRecipesForProduct} from "../Helper/Recipes";
import Input from "../../react/components/ui/spectre/Input";
import {getAllContainedFactories} from "../Helper/FacilityAggregators";
import FacilityRescaleModal from "./FacilityRescaleModal/Modal";

type Props = {
    facility: IFacility,
    reload: () => Promise<void>,
};

enum ModalType {
    AddFacility,
    EditFacility,
    RescaleFacility,
}

const translator = getTranslator('react/component/Satisfactory/FacilityDetailView');

function match(factory: Factory, search: string): boolean {
    const modifiers = search.match(/^[!<>@]+/)?.[0] ?? '';
    // Cut modifiers from search, and also trim any spaces
    search = search.substring(modifiers.length).replace(/^ +/g, '');

    const searchFactoryName = modifiers.includes('!') || modifiers === '';
    const searchIngredients = modifiers.includes('>') || modifiers === '';
    const searchProducts = modifiers.includes('<') || modifiers === '';
    const searchRecipes = modifiers.includes('@') || modifiers === '';

    if (searchFactoryName && factory.name.toLowerCase().includes(search)) {
        return true;
    }
    if (searchIngredients && factory.recipe.ingredients.some(itemStack => itemStack.item.toLowerCase().includes(search))) {
        return true;
    }
    if (searchProducts && factory.recipe.products.some(itemStack => itemStack.item.toLowerCase().includes(search))) {
        return true;
    }
    if (searchRecipes && factory.recipe.recipeType.toLowerCase().includes(search)) {
        return true;
    }

    return false;
}

export function FacilityDetailView({ facility, reload }: Props) {
    const [ modal, setModal ] = useState<ModalType|null>(null);
    const [ searchPrompt, setSearchPrompt ] = useState<string>('');
    const [ addFacilityItemStack, setAddFacilityItemStack ] = useState<ItemStack|null>(null);

    async function addFacility(inputPartialFacility: AddFacilityUserInput|AddFactoryUserInput) {
        const payload = {
            ...inputPartialFacility,
            parentFacility: facility.id,
        };
        switch (inputPartialFacility.type) {
            case 'Facility':
                await SatisfactoryAPI.Facility.addFacility(payload as AddFacilityPayload);
                break;
            case 'Factory':
                await SatisfactoryAPI.Facility.addFactory(payload as AddFactoryPayload);
                break;
        }
        await reload();
    }

    async function _editFacility(inputPartialFacility: EditFacilityUserInput|EditFactoryUserInput) {
        await editFacility(facility, inputPartialFacility);
        await reload();
    }

    async function deleteFacility() {
        await SatisfactoryAPI.Facility.deleteFacility(facility);
        await reload();
    }

    async function addFacilityForInput(item: Item, amount: number) {
        const recipes = getRecipesForProduct(facility.game, item);
        if (recipes.length > 0) {
            setAddFacilityItemStack({ item, amount });
        }
        setModal(ModalType.AddFacility);
    }

    // Reset special props when modal is closed
    useEffect(() => {
        if (modal === null) {
            setAddFacilityItemStack(null);
        }
    }, [modal]);

    let searchResults: Factory[] = [];
    if (searchPrompt !== '') {
        const factories = getAllContainedFactories(facility);
        searchResults = factories.filter((factory: Factory) => match(factory, searchPrompt.toLowerCase()));
    }

    return <>
        <FacilityPath facility={facility} />
        <div className='facilitySearchInputContainer'>
            <Input className='facilitySearchInput' onChange={e => setSearchPrompt(e.target.value)} />
            <div className='searchInlay'><Icon type='search' /></div>
        </div>
        <div className='facilityName'>
            {facility.name || translate('react/component/Satisfactory/General/unnamedFacility')}
            <div className="d-inline-block ml-2">
                <Button buttonTypes={[ButtonTypes.primary]} onClick={() => setModal(ModalType.AddFacility)}>
                    <Icon type='plus' />
                </Button>
                <Button buttonTypes={[ButtonTypes.primary]} onClick={() => setModal(ModalType.EditFacility)}>
                    <Icon type='edit' />
                </Button>
                <Button buttonTypes={[ButtonTypes.primary]} onClick={() => setModal(ModalType.RescaleFacility)}>
                    <Icon type='resize-vert' />
                </Button>
            </div>
        </div>
        {searchPrompt === '' && <>
            <div>
                <div className='facilityOverviewContainer'>
                    {
                        facility.type === 'Factory' && <div className='facilityOverviewCard'>
                            {facility.amount}x {facility.recipeType}
                        </div>
                    }
                    <div className='facilityOverviewCard'>
                        <FacilityIOSummary facility={facility} InputChildren={() => <FacilityIOButton
                            onClick={({item, amount}) => addFacilityForInput(item as Item, amount)}/>}/>
                    </div>
                </div>
            </div>
            {
                facility.type === "Facility" && <div>
                    <div>
                        Sub-Facilities
                    </div>
                    <div className='facilityOverviewContainer'>
                        {facility.facilities.map(facility =>
                            <div key={facility.id} className='facilityOverviewCard'>
                                <FacilityOverview reload={reload} facility={facility}/>
                            </div>)}
                    </div>
                </div>
            }
        </>}
        {searchPrompt !== '' && <>
            <div className='searchResultHeadline'>
                {translator('searchResults')}
            </div>
            <div>
                {
                    searchResults.map(result => {
                        return <FacilityOverview facility={result} reload={reload} showFullRoute={true} />
                    })
                }
            </div>
        </>}
        {modal === ModalType.AddFacility && <FacilityAddEditModal
            parentFacility={facility}
            save={addFacility}
            close={() => setModal(null)}
            delete={deleteFacility}
            targetProduct={addFacilityItemStack ?? undefined}
        />}
        {modal === ModalType.EditFacility && <FacilityAddEditModal
            save={_editFacility}
            close={() => setModal(null)}
            facility={facility}
            delete={deleteFacility}
        />}
        {modal === ModalType.RescaleFacility && facility.game === FactoryGameType.Satisfactory && <FacilityRescaleModal
            facility={facility}
            reload={reload}
            close={() => setModal(null)}
        />}
    </>
}
